import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../../common/AppBar.js";
import Snackbar from "../../../common/Snackbar.js";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import {
  HeadingWithTreePath,
  generateCompleteTreePath,
} from "../../SubassemblyDetails/utils/treePathUtils.js";

import "../../SubassemblyDetails/SubassemblyDetails.css";
import { getPages, renderPreview, renderVideo } from "../plugins/utils.js";
import {
  applyTransparency,
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../plugins/utils.js";
import RectangleElement from "../../Canvas/components/rectangle/RectangleElement.jsx";
import ArrowElement from "../../Canvas/components/arrow/ArrowElement.jsx";
import CircleElement from "../../Canvas/components/circle/CircleElement.jsx";
import LineElement from "../../Canvas/components/line/LineElement.jsx";
import CanvasImage from "../../Canvas/components/canvas-core/CanvasImage.jsx";
import TextElement from "../../Canvas/components/text/TextElement.jsx";
import DoubleSidedArrowElement from "../../Canvas/components/arrow/DoubleSidedArrowElement.jsx";
import CanvasDisplay from "../../Canvas/components/canvas-core/CanvasDisplay.jsx";
import { getMoveCopyInfo } from "../../../util/AppUtils.js";
import { getAllSectionsForPart } from "../../../redux/sectionSlice.js";
import {
  getAllAttachmentsForStep,
  getAllAttachmentsForSubstep,
  getAllCanvasesForStep,
  getAllCanvasesForSubstep,
  getAllFormsForStep,
  getAllFormsForSubstep,
  getAllRichTextsForStep,
  getAllRichTextsForSubstep,
  getAllStepsForSection,
  getAllSubstepsForStep,
  getAllVideosForStep,
  getAllVideosForSubstep,
} from "../../../redux/stepSlice.js";

/**
 * SIBLING ORDER LOGIC FOR MOVING CHILD NODES:
 *
 * When moving a child node (video, rich text, canvas, etc.) above or below another node:
 *
 * 1. SAME PARENT (same step/substep):
 *    - Move ABOVE: target_order - 1 (to position BEFORE target)
 *    - Move BELOW: target_order + 1 (to position AFTER target)
 *
 * 2. DIFFERENT PARENT (different step/substep):
 *    - Move ABOVE: target_order (no -1, just position ABOVE target)
 *    - Move BELOW: target_order + 1 (to position AFTER target)
 *
 * Examples:
 * - Moving Rich Text 3 below Video 4 (same step): from_order=3, to_order=3 (2+1)
 * - Moving Rich Text 3 above Rich Text 4 (same step): from_order=3, to_order=2 (3-1)
 * - Moving Rich Text 3 above Canvas 2 (different step): from_order=3, to_order=4 (no -1)
 * - Moving Rich Text 3 below Canvas 2 (different step): from_order=3, to_order=5 (4+1)
 */

const ProcedureRichTextMove = () => {
  const { bomTable } = useSelector((state) => state.parts);
  const { sections } = useSelector((state) => state.section);
  // Set up a consistent counter that will be incremented as we process items
  let globalRichTextCounter = 1;
  let canvasCounter = 1;
  let videoCounter = 1;
  let formCounter = 1;
  const [createCopy, setCreateCopy] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  let attachmentCounter = 1;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };
  const [targetSectionData, setTargetSectionData] = useState({});

  const {
    type,
    partNumber,
    description,
    revision,
    status,
    title,
    steps,
    revisionId,
    partId,
    fromSectionId,
    fromTreeId,
    fromSiblingOrder,
    fromStepSiblingOrder,
    fromStepId,
    fromStepTreeId,
    fromSubstepId,
    fromSubstepTreeId,
    fromSubstepSiblingOrder,
    elementId,
    elementTreeId,
  } = location.state;

  // Extract source information from title and construct the title early
  const titleParts = title.split(" ");

  // Extract step number - prioritize location.state.stepNumber for accuracy
  let sourceStepNumber =
    location.state.stepNumber ||
    (location.state.sourceStepIndex !== undefined
      ? location.state.sourceStepIndex + 1
      : 1);

  const sourceRichTextNumber = parseInt(titleParts[2]);

  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [selectedStepInfo, setSelectedStepInfo] = useState(null);
  const [selectedNodeInfo, setSelectedNodeInfo] = useState(null);
  const [selectionType, setSelectionType] = useState(null); // 'section', 'step', or 'node'
  const [toMoveCopyInfo, setToMoveCopy] = useState({});

  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const sortedSections = [...sections];

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreateCopyChange = () => {
    setCreateCopy(!createCopy);
  };

  const handleMoveUp = () => {
    if (!selectedNodeInfo) {
      showSnackbar("Please select a target node to move above", "error");
      return;
    }

    // Update direction for AppBar title
    setSelectedNodeInfo({
      ...selectedNodeInfo,
      direction: "up",
    });

    // ENHANCED SOURCE IDENTIFICATION: Scan all richTexts to verify source element
    const sourceId = location.state.elementId;
    let foundSourceRichText = null;
    let sourceContainerId = null;

    // Search in all containers
    Object.keys(richTexts).forEach((containerId) => {
      const textsInContainer = richTexts[containerId] || [];
      const found = textsInContainer.find(
        (rt) => rt.rich_text_node_id === sourceId
      );
      if (found) {
        foundSourceRichText = found;
        sourceContainerId = containerId;
      }
    });

    // If found, update location state with correct sibling_order
    if (foundSourceRichText) {
      location.state.elementSiblingOrder =
        foundSourceRichText.sibling_order ||
        foundSourceRichText.procedure_tree?.sibling_order ||
        location.state.elementSiblingOrder ||
        3; // Fallback to example value
    }

    // Extract source information from title correctly
    const titleParts = title.split(" ");
    // Get source step number from location state or from title
    const sourceStepNumber = location.state.stepNumber
      ? parseInt(location.state.stepNumber, 10)
      : parseInt(titleParts[1], 10);

    // Get source rich text number from location state or from title
    const sourceRichTextNumber = location.state.richTextNumber
      ? parseInt(location.state.richTextNumber, 10)
      : parseInt(titleParts[2], 10);

    // If sourceRichTextNumber is NaN, try alternative parsing
    let finalSourceNodeIndex = location.state.sourceNodeIndex;

    // Ensure we have a valid sourceStepId
    const sourceStepId =
      location.state.sourceStepId ||
      (steps && steps.length > 0 ? location.state.fromStepId : null);

    if (!sourceStepId) {
      console.warn("Could not determine source step ID");
    }

    // Check if we have the source element's sibling_order
    const sourceSiblingOrder = location.state.elementSiblingOrder;

    // Calculate the actual target node index in the combined list of all items
    const targetStepId = selectedNodeInfo.stepId;

    // Use the same logic as the UI to create the combined items list
    // This ensures the order matches exactly what the user sees
    const combinedItems = [
      ...(richTexts[targetStepId] || []).map((item) => ({
        ...item,
        type: "richText",
      })),
      ...(forms[targetStepId] || []).map((item) => ({
        ...item,
        type: "form",
      })),
      ...(attachments[targetStepId] || []).map((item) => ({
        ...item,
        type: "attachment",
      })),
      ...(canvases[targetStepId] || []).map((item) => ({
        ...item,
        type: "canvas",
      })),
      ...(videos[targetStepId] || []).map((item) => ({
        ...item,
        type: "video",
      })),
    ].filter((item) => !item.substep_id_ref && !item.sub_step_tree_node_id);

    // Apply the same sorting logic as the UI
    const targetStepItems = combinedItems.sort((a, b) => {
      // Get sibling orders, defaulting to empty string if not available
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order || "";
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order || "";

      // If both have sibling orders, compare them numerically
      if (orderA && orderB) {
        const orderCompare = compareSiblingOrders(orderA, orderB);
        if (orderCompare !== 0) return orderCompare;
      }

      // If only one has a sibling order, prioritize it
      if (orderA && !orderB) return -1;
      if (!orderA && orderB) return 1;

      // If no sibling orders, sort by created_at date if available
      const dateA = a.created_at ? new Date(a.created_at) : null;
      const dateB = b.created_at ? new Date(b.created_at) : null;

      if (dateA && dateB) return dateA - dateB; // Older items first

      // For consistent sorting when other methods fail, sort by type
      const typeOrder = {
        richText: 1,
        form: 2,
        attachment: 3,
        canvas: 4,
        video: 5,
      };
      return typeOrder[a.type] - typeOrder[b.type];
    });

    // Find the target item in the combined list
    let actualTargetNodeIndex = targetStepItems.length; // Default to end
    if (selectedNodeInfo.nodeId) {
      const targetItem = targetStepItems.find((item) => {
        const itemId =
          item.rich_text_node_id ||
          item.form_tree_node_id ||
          item.attachment_tree_node_id ||
          item.canvas_tree_node_id ||
          item.video_tree_node_id;
        return itemId === selectedNodeInfo.nodeId;
      });

      if (targetItem) {
        actualTargetNodeIndex = targetStepItems.indexOf(targetItem);
      }
    }

    // CRITICAL FIX: Calculate the correct sibling order for "move above" operations
    // For "move above", we need to calculate a sibling order that positions BEFORE the target
    let expectedSiblingOrder = 1; // Default value
    if (actualTargetNodeIndex < targetStepItems.length) {
      const targetItem = targetStepItems[actualTargetNodeIndex];
      const targetSiblingOrder =
        targetItem.sibling_order || targetItem.procedure_tree?.sibling_order;

      if (targetSiblingOrder) {
        const targetOrder = parseInt(targetSiblingOrder, 10);

        // Check if we're moving within the same step/substep (same parent)
        const isSameParent = sourceStepId === selectedNodeInfo.stepId;

        if (isSameParent) {
          // Moving within same parent: use target's sibling order - 1 to position BEFORE
          expectedSiblingOrder = Math.max(1, targetOrder - 1);
        } else {
          // Moving to different parent: use target's sibling order directly (no -1)
          expectedSiblingOrder = targetOrder;
        }
      }
    }

    // Navigate to preview screen
    navigate("/procedure-rich-text-move-preview", {
      state: {
        ...location.state,
        move_rev_node_info: {
          ...location.state.move_rev_node_info,
          ...toMoveCopyInfo,
          direction: "above",
        },
        moveType: "up",
        targetSectionIndex: selectedNodeInfo.sectionIndex,
        targetStepIndex: selectedNodeInfo.stepIndex,
        targetNodeIndex: actualTargetNodeIndex, // Use the calculated actual index
        targetNodeType: selectedNodeInfo.nodeType,
        targetNodeGlobalNumber: selectedNodeInfo.globalNodeNumber,
        targetNodeId: selectedNodeInfo.nodeId, // CRITICAL: Pass the target node ID for precise positioning
        expectedSiblingOrder: expectedSiblingOrder, // Pass the calculated sibling order
        sourceSiblingOrder: sourceSiblingOrder, // Pass the source node's current sibling order
        sourceStepIndex: location.state.sourceStepIndex,
        sourceSectionIndex: location.state.sourceSectionIndex,
        sourceNodeIndex: finalSourceNodeIndex,
        sourceNodeType: "rich_text",
        movingNode: true,
        sourceStepId: sourceStepId,
        targetStepId: selectedNodeInfo.stepId,
        sourceSubstepIndex: location.state.sourceSubstepIndex,
        revisionId,
        targetSectionData,
        partId,
        fromSectionId,
        fromTreeId,
        fromSiblingOrder,
        fromStepSiblingOrder,
        fromStepId,
        fromStepTreeId,
        fromSubstepId,
        elementId,
        elementTreeId,
        movingNodeType:
          selectedNodeInfo.nodeType === "Substep" ? "substep" : "step",
        fromSubstepTreeId: targetSectionData?.elementTreeId,
        fromSubstepSiblingOrder:
          location.state.elementSiblingOrder ||
          targetSectionData?.elementSiblingOrder,
        elementSiblingOrder: location.state.elementSiblingOrder, // Pass source sibling order directly
        createCopy,
        title: `Moving Rich Text ${sourceRichTextNumber} from Step ${sourceStepNumber}`,
        stepNumber: sourceStepNumber, // Add the actual step number
        ...(selectedNodeInfo.substepIndex !== undefined && {
          targetSubstepIndex: selectedNodeInfo.substepIndex,
        }),
      },
    });
  };

  const handleMoveInto = () => {
    if (
      !selectedStepInfo &&
      (!selectedNodeInfo || selectedNodeInfo.nodeType !== "Substep")
    ) {
      showSnackbar(
        "Please select a target step or substep to move into",
        "error"
      );
      return;
    }

    // Extract source information from title correctly
    const titleParts = title.split(" ");
    // Get source step number from location state or from title
    const sourceStepNumber = location.state.stepNumber
      ? parseInt(location.state.stepNumber, 10)
      : parseInt(titleParts[1], 10);

    // Get source rich text number from location state or from title
    const sourceRichTextNumber = location.state.richTextNumber
      ? parseInt(location.state.richTextNumber, 10)
      : parseInt(titleParts[2], 10);

    // Ensure we have a valid sourceStepId
    const sourceStepId =
      location.state.sourceStepId ||
      (steps && steps.length > 0 ? location.state.fromStepId : null);

    if (!sourceStepId) {
      console.warn("Could not determine source step ID");
    }

    // Check if we have the source element's sibling_order
    const sourceSiblingOrder = location.state.elementSiblingOrder;

    // Determine if target is a step or substep
    const isTargetSubstep =
      selectedNodeInfo && selectedNodeInfo.nodeType === "Substep";

    // If sourceRichTextNumber is NaN, try alternative parsing
    let finalSourceNodeIndex = location.state.sourceNodeIndex;

    navigate("/procedure-rich-text-move-preview", {
      state: {
        ...location.state,
        move_rev_node_info: {
          ...location.state.move_rev_node_info,
          ...toMoveCopyInfo,
          direction: "into",
        },
        moveType: "into",
        targetSectionIndex: isTargetSubstep
          ? selectedNodeInfo.sectionIndex
          : selectedStepInfo.sectionIndex,
        targetStepIndex: isTargetSubstep
          ? selectedNodeInfo.stepIndex
          : selectedStepInfo.stepIndex,
        sourceStepIndex: location.state.sourceStepIndex,
        sourceSectionIndex: location.state.sourceSectionIndex,
        sourceNodeIndex: finalSourceNodeIndex,
        sourceNodeType: "rich_text",
        title: `Moving Rich Text ${sourceRichTextNumber} from Step ${sourceStepNumber}`,
        stepNumber: sourceStepNumber, // Add the actual step number
        movingNode: true,
        sourceStepId: sourceStepId,
        targetStepId: isTargetSubstep
          ? selectedNodeInfo.stepId
          : selectedStepInfo?.stepId,
        sourceSubstepIndex: location.state.sourceSubstepIndex,
        revisionId,
        targetSectionData,
        partId,
        fromSectionId,
        fromTreeId,
        fromSiblingOrder,
        fromStepSiblingOrder,
        fromStepId,
        fromStepTreeId,
        fromSubstepId,
        elementTreeId,
        elementId,
        movingNodeType:
          selectedNodeInfo.nodeType === "Substep" ? "substep" : "step",
        fromSubstepTreeId: targetSectionData?.elementTreeId,
        fromSubstepSiblingOrder:
          location.state.elementSiblingOrder ||
          targetSectionData?.elementSiblingOrder,
        elementSiblingOrder: location.state.elementSiblingOrder, // Pass source sibling order directly
        createCopy,
        ...(isTargetSubstep && {
          targetSubstepIndex: selectedNodeInfo.substepIndex,
        }),
      },
    });
  };

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      // setShowLoader(true);

      await dispatch(getAllSectionsForPart({ partId, revisionId }));
    })();
  }, [dispatch, partId, revisionId]);

  useEffect(() => {
    if (sections.length > 0) {
      sections.forEach(async (section, index) => {
        await dispatch(
          getAllStepsForSection({
            partId,
            revisionId,
            sectionId: section.section_tree_node_id,
          })
        );
      });
    }
  }, [sections, dispatch, partId, revisionId]);

  useEffect(() => {
    if (Object.keys(stepsBySection).length > 0) {
      Object.entries(stepsBySection).forEach(([sectionId, steps], index) => {
        steps.forEach(async (step) => {
          const { step_tree_node_id: stepId } = step;

          // // Fetch for step-level content
          await dispatch(
            getAllRichTextsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllFormsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllVideosForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllCanvasesForStep({ partId, revisionId, sectionId, stepId })
          );
          // Fetch substeps (async)
          await dispatch(
            getAllSubstepsForStep({ partId, revisionId, sectionId, stepId })
          ).then((action) => {
            const substeps = action.payload?.substeps || [];
            substeps.forEach((substep) => {
              const substepId = substep.sub_step_tree_node_id;

              dispatch(
                getAllRichTextsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllFormsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllVideosForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllAttachmentsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllCanvasesForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
            });
          });
          if (index === Object.entries(stepsBySection).length - 1) {
            // setShowLoader(false);
          }
        });
      });
    } else {
      // setShowLoader(false);
    }
  }, [sections, dispatch, partId, revisionId, stepsBySection]);

  const handleMoveBelow = () => {
    if (!selectedNodeInfo) {
      showSnackbar("Please select a target node to move below", "error");
      return;
    }

    // Update direction for AppBar title
    setSelectedNodeInfo({
      ...selectedNodeInfo,
      direction: "below",
    });

    // ENHANCED SOURCE IDENTIFICATION: Scan all richTexts to verify source element
    const sourceId = location.state.elementId;
    let foundSourceRichText = null;
    let sourceContainerId = null;

    // Search in all containers
    Object.keys(richTexts).forEach((containerId) => {
      const textsInContainer = richTexts[containerId] || [];
      const found = textsInContainer.find(
        (rt) => rt.rich_text_node_id === sourceId
      );
      if (found) {
        foundSourceRichText = found;
        sourceContainerId = containerId;
      }
    });

    // If found, update location state with correct sibling_order
    if (foundSourceRichText) {
      location.state.elementSiblingOrder =
        foundSourceRichText.sibling_order ||
        foundSourceRichText.procedure_tree?.sibling_order ||
        location.state.elementSiblingOrder ||
        3; // Fallback to example value
    }

    // If sourceRichTextNumber is NaN, try alternative parsing
    let finalSourceNodeIndex = location.state.sourceNodeIndex;

    // Ensure we have a valid sourceStepId
    const sourceStepId =
      location.state.sourceStepId ||
      (steps && steps.length > 0 ? location.state.fromStepId : null);

    if (!sourceStepId) {
      console.warn("Could not determine source step ID");
    }

    // Check if we have the source element's sibling_order
    const sourceSiblingOrder = location.state.elementSiblingOrder;

    // Navigate to preview screen
    navigate("/procedure-rich-text-move-preview", {
      state: {
        ...location.state,
        move_rev_node_info: {
          ...location.state.move_rev_node_info,
          ...toMoveCopyInfo,
          direction: "below",
        },
        moveType: "below",
        targetSectionIndex: selectedNodeInfo.sectionIndex,
        targetStepIndex: selectedNodeInfo.stepIndex,
        targetNodeIndex: parseInt(selectedNodeInfo.nodeIndex.toString()), // Keep actual index for below move
        targetNodeType: selectedNodeInfo.nodeType,
        targetNodeGlobalNumber: selectedNodeInfo.globalNodeNumber,
        sourceStepIndex: location.state.sourceStepIndex,
        sourceSectionIndex: location.state.sourceSectionIndex,
        sourceNodeIndex: finalSourceNodeIndex,
        sourceNodeType: "rich_text",
        movingNode: true,
        sourceStepId: sourceStepId,
        targetStepId: selectedNodeInfo.stepId,
        sourceSubstepIndex: location.state.sourceSubstepIndex,
        revisionId,
        targetSectionData,
        partId,
        elementTreeId,
        fromSectionId,
        fromTreeId,
        elementId,
        movingNodeType:
          selectedNodeInfo.nodeType === "Substep" ? "substep" : "step",
        fromSiblingOrder,
        fromStepSiblingOrder,
        fromStepId,
        fromStepTreeId,
        fromSubstepId,
        fromSubstepTreeId: targetSectionData?.elementTreeId,
        fromSubstepSiblingOrder:
          location.state.elementSiblingOrder ||
          targetSectionData?.elementSiblingOrder,
        elementSiblingOrder: location.state.elementSiblingOrder, // Pass source sibling order directly
        createCopy,
        title: `Moving Rich Text ${sourceRichTextNumber} from Step ${sourceStepNumber}`,
        stepNumber: sourceStepNumber, // Add the actual step number
        ...(selectedNodeInfo.substepIndex !== undefined && {
          targetSubstepIndex: selectedNodeInfo.substepIndex,
        }),
      },
    });
  };

  // Get the buttons to display based on selection type
  const getButtons = () => {
    const buttons = [];

    if (
      selectionType === "step" ||
      (selectionType === "node" &&
        selectedNodeInfo &&
        selectedNodeInfo.nodeType === "Substep")
    ) {
      // Show Move Into/Copy Into button when step or substep is selected
      buttons.push({
        label: createCopy ? "Paste Into" : "Move Into",
        onClick: handleMoveInto,
      });
    } else if (selectionType === "node") {
      // Only show Move Up/Copy Up and Move Below/Copy Below buttons when node is selected (but not a substep)
      buttons.push({
        label: createCopy ? "Paste Above" : "Move Up",
        onClick: handleMoveUp,
      });
      buttons.push({
        label: createCopy ? "Paste Below" : "Move Below",
        onClick: handleMoveBelow,
      });
    }

    return buttons;
  };

  return (
    <div>
      {/* Debug display to check selected node info */}
      {selectedNodeInfo && (
        <div
          style={
            {
              // position: "fixed",
              // top: 0,
              // left: 0,
              // background: "rgba(0,0,0,0.7)",
              // color: "white",
              // padding: "10px",
              // zIndex: 9999,
            }
          }
        >
          {/* Selected: {selectedNodeInfo.nodeType} (ID: {selectedNodeInfo.nodeId}) */}
        </div>
      )}
      <AppBar
        showTablesDropdown={false}
        showBackButton={false}
        showCancelButton={true}
        showCreateCopyCheckbox={true}
        createCopy={createCopy}
        onCreateCopyChange={handleCreateCopyChange}
        title={`${createCopy ? "Copying" : "Moving"} ${title} ${
          selectionType === "step" && selectedStepInfo
            ? `into Step ${selectedStepInfo.globalStepNumber}`
            : selectionType === "node" &&
              selectedNodeInfo &&
              selectedNodeInfo.nodeType === "Substep"
            ? `into ${selectedNodeInfo.nodeType} ${selectedNodeInfo.globalNodeNumber}`
            : selectionType === "node" && selectedNodeInfo
            ? `${selectedNodeInfo.direction === "up" ? "above" : "to"} ${
                selectedNodeInfo.nodeType
              } ${selectedNodeInfo.globalNodeNumber}`
            : ""
        }`}
        buttons={getButtons()}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 0",
          flexWrap: "wrap", // for responsiveness
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            {/* Hide procedure checkbox */}
            <span style={{ fontSize: "20px", fontWeight: 600 }}>
              {type} {partNumber}
            </span>
          </label>

          {/* Description */}
          {/* <span style={{ marginLeft: "2px", marginTop: "5px", color: "#666" }}>
            Description: {description}
          </span> */}
        </div>

        {/* Right-aligned section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          <div>
            <strong>Status: </strong>
            <span style={{ color: "orange" }}>Not Submitted</span>
          </div>

          <div>
            <strong>Revision: </strong>
            <span>{revision}</span>
          </div>
        </div>
      </div>

      {sortedSections
        .sort((a, b) => {
          const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
          const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

          const orderCompare = compareSiblingOrders(orderA, orderB);
          if (orderCompare !== 0) return orderCompare;

          // Fallback: compare created_at
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA; // latest comes first
        })
        .map((section, index) => {
          const selectedSection =
            index + 1 === selectedSectionIndex ||
            location.state.fromSectionId == section.section_tree_node_id;

          const isDisabled = selectedSection || index === selectedSectionIndex;
          const sectionHeading = `Section ${index + 1}`;
          return (
            <div
              key={section.section_tree_node_id}
              style={{
                margin: "20px 0",
                clear: "both",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  clear: "both",
                }}
              >
                {/* Hide section checkbox */}
                {/* Simple heading for sections without tree path */}
                <h2 style={{ fontSize: "20px", margin: 0 }}>
                  {sectionHeading}
                </h2>
              </label>
              <hr />
              <hr />
              <div>{section.section_name}</div>
              <div>{section.short_description}</div>

              {/* Steps for this section */}
              {(stepsBySection[section.section_tree_node_id] || []).map(
                (step, stepIndex) => {
                  const stepId = step.step_tree_node_id;

                  const stepsBeforeThisSection = sortedSections
                    .slice(0, index)
                    .reduce(
                      (acc, s) =>
                        acc +
                        (stepsBySection[s.section_tree_node_id]?.length || 0),
                      0
                    );
                  const globalStepNumber =
                    stepsBeforeThisSection + stepIndex + 1;

                  // Check if this is the source step that contains the attachment being moved
                  const isSourceStep = globalStepNumber === sourceStepNumber;

                  const myVideos = videos[stepId] || [];
                  const myForms = forms[stepId] || [];
                  const myCanvases = canvases[stepId] || [];
                  const myRichTexts = richTexts[stepId] || [];

                  const combinedItems = [
                    ...(richTexts[stepId] || []).map((item) => ({
                      ...item,
                      type: "richText",
                    })),
                    ...(forms[stepId] || []).map((item) => ({
                      ...item,
                      type: "form",
                    })),
                    ...(attachments[stepId] || []).map((item) => ({
                      ...item,
                      type: "attachment",
                    })),
                    ...(canvases[stepId] || []).map((item) => ({
                      ...item,
                      type: "canvas",
                    })),
                    ...(videos[stepId] || []).map((item) => ({
                      ...item,
                      type: "video",
                    })),
                  ]; // Filter out substeps or unwanted items (same as your current filter)
                  const filteredItems = combinedItems.filter(
                    (item) =>
                      !item.substep_id_ref && !item.sub_step_tree_node_id
                  );

                  filteredItems.sort((a, b) => {
                    const orderA =
                      a.sibling_order || a.procedure_tree?.sibling_order;
                    const orderB =
                      b.sibling_order || b.procedure_tree?.sibling_order;

                    const orderCompare = compareSiblingOrders(orderA, orderB);
                    if (orderCompare !== 0) return orderCompare;

                    // Fallback: compare created_at
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);
                    return dateB - dateA; // latest comes first
                  });
                  const stepHeading = `Step ${globalStepNumber}`;
                  return (
                    <div
                      key={stepId}
                      style={{ marginTop: "20px", clear: "both" }}
                    >
                      <label
                        style={{
                          display: "flex",
                          gap: "8px",
                          cursor: "pointer",
                          clear: "both",
                        }}
                      >
                        <input
                          type="checkbox"
                          onChange={() => {
                            if (
                              selectedStepInfo &&
                              selectedStepInfo.stepId === stepId
                            ) {
                              // Clear step selection
                              setSelectedStepInfo(null);
                              setSelectionType(null);
                              setTargetSectionData({});
                            } else {
                              // Clear any node selection when selecting a step
                              setSelectedNodeInfo(null);
                              setSelectedStepInfo({
                                stepId,
                                sectionIndex: index,
                                stepIndex: stepIndex,
                                globalStepNumber,
                                nodeType: "Step",
                              });
                              setSelectedNodeInfo({
                                nodeId: stepId,
                                nodeType: "Step",
                                sectionIndex: index,
                                stepIndex: stepIndex,
                                globalStepNumber,
                              });
                              setTargetSectionData({
                                sectionId: section.section_tree_node_id,
                                siblingOrder: section.sibling_order,
                                sectionTreeId: section.section_tree_node_id,
                                stepId,
                                stepTreeId: step.procedure_tree_ref,
                                stepSiblingOrder:
                                  step.sibling_order ||
                                  step.procedure_tree?.sibling_order,
                              });
                              setSelectionType("step");
                            }
                            setToMoveCopy(
                              getMoveCopyInfo(
                                "to",
                                sectionHeading,
                                stepHeading,
                                null,
                                null
                              )
                            );
                          }}
                          checked={
                            selectedStepInfo &&
                            selectedStepInfo.stepId === stepId
                          }
                          disabled={false} // Steps should always be selectable/unselectable
                        />

                        <HeadingWithTreePath
                          title={stepHeading}
                          fontSize="20px" // Use consistent font size with child nodes
                          treePathParams={{
                            sectionIndex: index,
                            stepIndex: globalStepNumber - 1,
                            substepIndex: undefined,
                            nodeType: undefined,
                            nodeIndex: undefined,
                          }}
                          color="#0d6efd" // Blue color for all steps
                          clear="both"
                        />
                      </label>
                      <hr />
                      <p style={{ whiteSpace: 'pre-wrap' }}>{step.short_description || ""}</p>
                      <p>
                        <p>
                          {structuredClone(step.step_details)
                            .sort(
                              (a, b) =>
                                new Date(a.created_at) - new Date(b.created_at)
                            )
                            .map((myStep) => {
                              const partNumber =
                                myStep.part_info?.part_number || "";
                              const description =
                                myStep.part_info?.description || "";
                              const qtyText =
                                myStep.qty && myStep.qty != 0
                                  ? `Qty ${myStep.qty}`
                                  : "";

                              const partText = partNumber
                                ? ` (${partNumber})`
                                : "";

                              return (
                                <>
                                  {myStep.action}
                                  {partText}{" "}
                                  <RenderHtml content={description} /> {qtyText}
                                  &nbsp;
                                </>
                              );
                            })}
                        </p>
                      </p>

                      <h4>{step.bom_table_title_info?.category_name || ""}</h4>

                      <table className="table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>
                              {
                                step.part_number_column_title_ref_info
                                  ?.category_name
                              }{" "}
                            </th>
                            <th>Description</th>
                            <th>Qty Per</th>
                            <th>Unit</th>
                          </tr>
                        </thead>

                        <tbody>
                          {step.parts_required.map((row) => (
                            <tr>
                              <td>{row.part_info.type_info.name}</td>
                              <td>{row.part_info.part_number}</td>
                              <td>
                                <RenderHtml
                                  content={row.part_info.description}
                                />{" "}
                              </td>
                              <td>{row.qty === 0 ? "" : row.qty}</td>
                              <td>{row.part_info?.unit_info?.unit_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Step Content */}
                      {filteredItems.map((item, itemIndex) => {
                        if (item.substep_id_ref || item.sub_step_tree_node_id)
                          return null;

                        // Pre-calculate node numbers for comparison before incrementing counters
                        const currentAttachmentNumber = attachmentCounter;
                        const currentRichTextNumber = globalRichTextCounter;
                        const currentCanvasNumber = canvasCounter;
                        const currentVideoNumber = videoCounter;
                        const currentFormNumber = formCounter;

                        // Determine if this is the source attachment being moved
                        const isSourceItem =
                          isSourceStep &&
                          ((item.type === "richText" &&
                            currentRichTextNumber === sourceRichTextNumber) ||
                            (item.type === "form" &&
                              currentFormNumber === sourceRichTextNumber) ||
                            (item.type === "canvas" &&
                              currentCanvasNumber === sourceRichTextNumber) ||
                            (item.type === "video" &&
                              currentVideoNumber === sourceRichTextNumber) ||
                            (item.type === "attachment" &&
                              currentAttachmentNumber ===
                                sourceRichTextNumber));

                        switch (item.type) {
                          case "richText": {
                            // Use the current counter value
                            const globalRichTextNumber =
                              globalRichTextCounter++;
                            // Create a unique ID if rich_text_node_id is undefined
                            const richTextId =
                              item.rich_text_node_id ||
                              `rich-text-${globalRichTextNumber}`;
                            // render richText block here
                            const isMovingThisAttachment =
                              (isSourceStep &&
                                globalRichTextNumber ===
                                  sourceRichTextNumber) ||
                              title.split(" ")[2] ===
                                globalRichTextNumber.toString();
                            const nodeHeading = `Rich Text ${globalRichTextNumber}`;
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                fontSize="20px" // Use consistent font size
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "richText",
                                  nodeIndex: globalRichTextNumber - 1,
                                }}
                                color={
                                  isMovingThisAttachment ? undefined : "#0d6efd"
                                } // Blue color only for items NOT being moved
                              />
                            );

                            return (
                              <div
                                key={richTextId}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId === richTextId
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        // Ensure we're using a consistent string format
                                        const nodeType = "Rich Text";
                                        // Set selection type first
                                        setSelectionType("node");

                                        // Check if this is the source item being moved
                                        if (isMovingThisAttachment) {
                                          // Store the real sibling_order of the source Rich Text
                                          const siblingOrder =
                                            item.sibling_order ||
                                            item.procedure_tree
                                              ?.sibling_order ||
                                            0;
                                          location.state.elementSiblingOrder =
                                            siblingOrder;
                                        }

                                        // Then set the selected node info
                                        setSelectedNodeInfo({
                                          nodeId: richTextId,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber:
                                            globalRichTextNumber,
                                          nodeType: "Rich Text",
                                          direction: null,
                                          stepId,
                                        });

                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId: item.rich_text_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      isMovingThisAttachment ||
                                      (selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          richTextId &&
                                        selectedNodeInfo.nodeType ===
                                          "Rich Text") ||
                                      false
                                    }
                                    disabled={false}
                                    style={
                                      isMovingThisAttachment
                                        ? {
                                            accentColor: "#444444", // Dark grey for item being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                  />
                                  {label}
                                </label>

                                <div
                                  style={{
                                    marginLeft: "5px",
                                    marginTop: "-10px",
                                    clear: "both",
                                  }}
                                >
                                  {item.short_description}
                                </div>
                                <div
                                  style={{
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                    clear: "both",
                                  }}
                                >
                                  {item.category_data?.category_name}
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                    clear: "both",
                                  }}
                                >
                                  <div
                                    style={{ marginTop: "-6px", clear: "both" }}
                                    dangerouslySetInnerHTML={{
                                      __html: item.rich_text_content,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          }
                          case "video": {
                            const globalVideoNumber = videoCounter++; // ✅ use then increment
                            const nodeHeading = `Video ${globalVideoNumber}`;
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                fontSize="20px" // Use consistent font size
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "video",
                                  nodeIndex: globalVideoNumber - 1,
                                }}
                                color="#0d6efd" // Blue color for all items
                              />
                            );

                            return (
                              <div
                                key={item.video_tree_node_id || label}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.video_tree_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        // Clear any step selection when selecting a substep
                                        setSelectedStepInfo(null);
                                        setSelectedNodeInfo({
                                          nodeId: item.video_tree_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber: globalVideoNumber,
                                          nodeType: "Video",
                                          direction: null,
                                          stepId,
                                        });
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId: item.video_tree_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      (selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.video_tree_node_id &&
                                        selectedNodeInfo.nodeType ===
                                          "Video") ||
                                      false
                                    }
                                    disabled={false} // Video nodes should always be selectable/unselectable
                                    style={
                                      isSourceItem
                                        ? {
                                            accentColor: "#444444", // Dark grey for video being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                  />
                                  {label}
                                </label>

                                {renderVideo(item)}

                                <div>{item.video_url}</div>

                                <div>{item.short_description}</div>
                              </div>
                            );
                          }

                          case "form": {
                            const globalFormNumber = formCounter++; // ✅ use then increment
                            const nodeHeading = `Form ${globalFormNumber}`;
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                fontSize="20px" // Use consistent font size
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "form",
                                  nodeIndex: globalFormNumber - 1,
                                }}
                                color="#0d6efd" // Blue color for all items
                              />
                            );

                            return (
                              <div
                                key={item.form_tree_node_id || label}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    marginTop: "20px",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.form_tree_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        // Clear any step selection when selecting a substep
                                        setSelectedStepInfo(null);
                                        setSelectedNodeInfo({
                                          nodeId: item.form_tree_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber: globalFormNumber,
                                          nodeType: "Form",
                                          direction: null,
                                          stepId,
                                        });
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId: item.form_tree_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      (selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.form_tree_node_id &&
                                        selectedNodeInfo.nodeType === "Form") ||
                                      false
                                    }
                                    disabled={false} // Form nodes should always be selectable/unselectable
                                    style={
                                      isSourceItem
                                        ? {
                                            accentColor: "#444444", // Dark grey for form being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                  />
                                  {label}
                                </label>
                                <div>{item.short_description}</div>
                              </div>
                            );
                          }
                          case "canvas": {
                            const pages = getPages(item);

                            const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                            const nodeHeading = `Canvas ${globalCanvasNumber}`;
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                fontSize="20px" // Use consistent font size
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "canvas",
                                  nodeIndex: globalCanvasNumber - 1,
                                }}
                                color="#0d6efd" // Blue color for all items
                              />
                            );

                            return (
                              <div
                                key={item.canvas_tree_node_id || label}
                                style={{ marginTop: "40px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.canvas_tree_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        // Clear any step selection when selecting a substep
                                        setSelectedStepInfo(null);
                                        setSelectedNodeInfo({
                                          nodeId: item.canvas_tree_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber: globalCanvasNumber,
                                          nodeType: "Canvas",
                                          direction: null,
                                          stepId,
                                        });
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId: item.canvas_tree_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      (selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.canvas_tree_node_id &&
                                        selectedNodeInfo.nodeType ===
                                          "Canvas") ||
                                      false
                                    }
                                    disabled={false} // Canvas nodes should always be selectable/unselectable
                                    style={
                                      isSourceItem
                                        ? {
                                            accentColor: "#444444", // Dark grey for canvas being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                  />
                                  {label}
                                </label>

                                {/* Display all pages vertically */}
                                <CanvasDisplay pages={pages} />
                              </div>
                            );
                          }
                          case "attachment": {
                            const attachmentList = item.attachments || [];
                            if (attachmentList.length === 0) return null;

                            const globalAttachmentNumber = attachmentCounter;
                            attachmentCounter++; // Increment after using the value

                            const nodeHeading = `Attachment ${globalAttachmentNumber}`;
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                fontSize="20px" // Use consistent font size
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "attachment",
                                  nodeIndex: globalAttachmentNumber - 1,
                                }}
                                color="#0d6efd" // Blue color for all items
                              />
                            );

                            return (
                              <div
                                key={
                                  item.attachment_tree_node_id ||
                                  `attachment-${itemIndex}`
                                }
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    marginTop: "20px",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.attachment_tree_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        setSelectedNodeInfo({
                                          nodeId: item.attachment_tree_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber:
                                            globalAttachmentNumber,
                                          nodeType: "Attachment",
                                          direction: null,
                                          stepId,
                                        });
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId:
                                            item.attachment_tree_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      (selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.attachment_tree_node_id &&
                                        selectedNodeInfo.nodeType ===
                                          "Attachment") ||
                                      false
                                    }
                                    disabled={false} // Attachment nodes should always be selectable/unselectable
                                    style={
                                      isSourceItem
                                        ? {
                                            accentColor: "#444444", // Dark grey for attachment being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                  />
                                  {label}
                                </label>
                                <div>{item.short_description}</div>
                                {attachmentList.map((attachment, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      marginLeft: "20px",
                                      clear: "both",
                                    }}
                                  >
                                    {renderPreview(attachment)}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          default:
                            return null;
                        }
                      })}

                      {/* Substeps */}
                      {(substepsByStep[stepId] || []).map(
                        (substep, substepIndex) => {
                          const substepId = substep.sub_step_tree_node_id;
                          const combinedItems = [
                            ...(richTexts[substepId] || []).map((item) => ({
                              ...item,
                              type: "richText",
                            })),
                            ...(forms[substepId] || []).map((item) => ({
                              ...item,
                              type: "form",
                            })),
                            ...(attachments[substepId] || []).map((item) => ({
                              ...item,
                              type: "attachment",
                            })),
                            ...(canvases[substepId] || []).map((item) => ({
                              ...item,
                              type: "canvas",
                            })),
                            ...(videos[substepId] || []).map((item) => ({
                              ...item,
                              type: "video",
                            })),
                          ]; // Filter out substeps or unwanted items (same as your current filter)
                          const filteredItems = combinedItems;

                          filteredItems.sort((a, b) => {
                            const orderA =
                              a.sibling_order ||
                              a.procedure_tree?.sibling_order;
                            const orderB =
                              b.sibling_order ||
                              b.procedure_tree?.sibling_order;

                            const orderCompare = compareSiblingOrders(
                              orderA,
                              orderB
                            );
                            if (orderCompare !== 0) return orderCompare;

                            // Fallback: compare created_at
                            const dateA = new Date(a.created_at);
                            const dateB = new Date(b.created_at);
                            return dateB - dateA; // latest comes first
                          });

                          // Global step number (same logic you already use for steps)
                          const stepsBeforeThisOne =
                            sortedSections
                              .slice(0, index)
                              .reduce((sectionAcc, s) => {
                                const steps =
                                  stepsBySection[s.section_tree_node_id] || [];
                                return sectionAcc + steps.length;
                              }, 0) + stepIndex;

                          const globalStepNumber = stepsBeforeThisOne + 1;

                          // Final substep label like Substep 5A
                          const substepLetter = String.fromCharCode(
                            65 + substepIndex
                          ); // A, B, C, ...

                          const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;

                          return (
                            <div
                              key={substepId}
                              style={{ marginTop: "20px", clear: "both" }}
                            >
                              <div>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId === substepId
                                      ) {
                                        // Deselect if already selected
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                        setTargetSectionData({});
                                      } else {
                                        // Clear any step selection when selecting a substep
                                        setSelectedStepInfo(null);
                                        setSelectedNodeInfo({
                                          nodeId: substepId,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          substepIndex: substepIndex,
                                          globalNodeNumber: `${globalStepNumber}${substepLetter}`,
                                          nodeType: "Substep",
                                          direction: null,
                                          stepId,
                                        });
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          substepId,
                                          substepTreeId:
                                            substep.procedure_tree_ref,
                                          substepSiblingOrder:
                                            substep.sibling_order ||
                                            substep.procedure_tree
                                              ?.sibling_order,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          globalSubstepLabel,
                                          null
                                        )
                                      );
                                    }}
                                    checked={
                                      selectedNodeInfo &&
                                      selectedNodeInfo.nodeType === "Substep" &&
                                      selectedNodeInfo.nodeId === substepId
                                    }
                                    disabled={false} // Substeps should always be selectable/unselectable
                                  />
                                  <HeadingWithTreePath
                                    title={globalSubstepLabel}
                                    fontSize="20px" // Use consistent font size with child nodes
                                    treePathParams={{
                                      sectionIndex: index,
                                      stepIndex: globalStepNumber - 1,
                                      substepIndex: substepIndex,
                                      nodeType: undefined,
                                      nodeIndex: undefined,
                                    }}
                                    color="#0d6efd" // Blue color for all substeps
                                  />
                                </label>

                                <div style={{ whiteSpace: 'pre-wrap' }}>{substep.short_description}</div>
                              </div>

                              {/* Substep content */}
                              {filteredItems.map((item, itemIndex) => {
                                // Pre-calculate node numbers for comparison before incrementing counters
                                const currentAttachmentNumber =
                                  attachmentCounter;
                                const currentRichTextNumber =
                                  globalRichTextCounter;
                                const currentCanvasNumber = canvasCounter;
                                const currentVideoNumber = videoCounter;
                                const currentFormNumber = formCounter;

                                // Determine if this is the source attachment being moved
                                const isSourceItem =
                                  isSourceStep &&
                                  ((item.type === "richText" &&
                                    currentRichTextNumber ===
                                      sourceRichTextNumber) ||
                                    (item.type === "form" &&
                                      currentFormNumber ===
                                        sourceRichTextNumber) ||
                                    (item.type === "canvas" &&
                                      currentCanvasNumber ===
                                        sourceRichTextNumber) ||
                                    (item.type === "video" &&
                                      currentVideoNumber ===
                                        sourceRichTextNumber) ||
                                    (item.type === "attachment" &&
                                      currentAttachmentNumber ===
                                        sourceRichTextNumber));

                                switch (item.type) {
                                  case "richText": {
                                    // Use the current counter value
                                    const globalRichTextNumber =
                                      globalRichTextCounter++;
                                    // Create a unique ID if rich_text_node_id is undefined
                                    const richTextId =
                                      item.rich_text_node_id ||
                                      `rich-text-${globalRichTextNumber}`;
                                    // render richText block here
                                    const isMovingThisAttachment =
                                      (isSourceStep &&
                                        globalRichTextNumber ===
                                          sourceRichTextNumber) ||
                                      title.split(" ")[2] ===
                                        globalRichTextNumber.toString();
                                    const nodeHeading = `Rich Text ${globalRichTextNumber}`;
                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        fontSize="20px" // Use consistent font size
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "richText",
                                          nodeIndex: globalRichTextNumber - 1,
                                        }}
                                        color={
                                          isMovingThisAttachment
                                            ? undefined
                                            : "#0d6efd"
                                        } // Blue color only for items NOT being moved
                                      />
                                    );

                                    return (
                                      <div
                                        key={richTextId}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  richTextId
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                // Ensure we're using a consistent string format
                                                const nodeType = "Rich Text";
                                                // Set selection type first
                                                setSelectionType("node");

                                                // Check if this is the source item being moved
                                                if (isMovingThisAttachment) {
                                                  // Store the real sibling_order of the source Rich Text
                                                  const siblingOrder =
                                                    item.sibling_order ||
                                                    item.procedure_tree
                                                      ?.sibling_order ||
                                                    0;
                                                  location.state.elementSiblingOrder =
                                                    siblingOrder;
                                                }

                                                // Then set the selected node info
                                                setSelectedNodeInfo({
                                                  nodeId: richTextId,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex,
                                                  globalNodeNumber:
                                                    globalRichTextNumber,
                                                  nodeType: "Rich Text",
                                                  direction: null,
                                                  substepIndex: substepIndex,
                                                  stepId,
                                                });

                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.rich_text_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                });
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              isMovingThisAttachment ||
                                              (selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  richTextId &&
                                                selectedNodeInfo.nodeType ===
                                                  "Rich Text")
                                            }
                                            disabled={false}
                                            style={
                                              isMovingThisAttachment
                                                ? {
                                                    accentColor: "#444444", // Dark grey for item being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : {}
                                            }
                                          />
                                          {label}
                                        </label>

                                        <div
                                          style={{
                                            marginLeft: "5px",
                                            marginTop: "-10px",
                                            clear: "both",
                                          }}
                                        >
                                          {item.short_description}
                                        </div>
                                        <div
                                          style={{
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                            clear: "both",
                                          }}
                                        >
                                          {item.category_data?.category_name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                            clear: "both",
                                          }}
                                        >
                                          <div
                                            style={{
                                              marginTop: "-6px",
                                              clear: "both",
                                            }}
                                            dangerouslySetInnerHTML={{
                                              __html: item.rich_text_content,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  }
                                  case "video": {
                                    const globalVideoNumber = videoCounter++; // ✅ use then increment
                                    const nodeHeading = `Video ${globalVideoNumber}`;
                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        fontSize="20px" // Use consistent font size
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "video",
                                          nodeIndex: globalVideoNumber - 1,
                                        }}
                                        color="#0d6efd" // Blue color for all items
                                      />
                                    );

                                    return (
                                      <div
                                        key={item.video_tree_node_id || label}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.video_tree_node_id
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                // Clear any step selection when selecting a substep
                                                setSelectedStepInfo(null);
                                                setSelectedNodeInfo({
                                                  nodeId:
                                                    item.video_tree_node_id,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex,
                                                  globalNodeNumber:
                                                    globalVideoNumber,
                                                  nodeType: "Video",
                                                  direction: null,
                                                  substepIndex: substepIndex,
                                                  stepId,
                                                });
                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  substepId,
                                                  substepTreeId:
                                                    substep.procedure_tree_ref,
                                                  substepSiblingOrder:
                                                    substep.sibling_order ||
                                                    substep.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.video_tree_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                });
                                                setSelectionType("node");
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              (selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.video_tree_node_id &&
                                                selectedNodeInfo.nodeType ===
                                                  "Video") ||
                                              false
                                            }
                                            disabled={false} // Substep Video nodes should always be selectable/unselectable
                                            style={
                                              isSourceItem
                                                ? {
                                                    accentColor: "#444444", // Dark grey for video being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : {}
                                            }
                                          />
                                          {label}
                                        </label>

                                        {renderVideo(item)}

                                        <div>{item.video_url}</div>

                                        <div>{item.short_description}</div>
                                      </div>
                                    );
                                  }

                                  case "form": {
                                    const globalFormNumber = formCounter++; // ✅ use then increment
                                    const nodeHeading = `Form ${globalFormNumber}`;
                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        fontSize="20px" // Use consistent font size
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "form",
                                          nodeIndex: globalFormNumber - 1,
                                        }}
                                        color="#0d6efd" // Blue color for all items
                                      />
                                    );

                                    return (
                                      <div
                                        key={item.form_tree_node_id || label}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.form_tree_node_id
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                // Clear any step selection when selecting a substep
                                                setSelectedStepInfo(null);
                                                setSelectedNodeInfo({
                                                  nodeId:
                                                    item.form_tree_node_id,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex,
                                                  globalNodeNumber:
                                                    globalFormNumber,
                                                  nodeType: "Form",
                                                  direction: null,
                                                  substepIndex: substepIndex,
                                                  stepId,
                                                });
                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  substepId,
                                                  substepTreeId:
                                                    substep.procedure_tree_ref,
                                                  substepSiblingOrder:
                                                    substep.sibling_order ||
                                                    substep.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.form_tree_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                });
                                                setSelectionType("node");
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              (selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.form_tree_node_id &&
                                                selectedNodeInfo.nodeType ===
                                                  "Form") ||
                                              false
                                            }
                                            disabled={false} // Substep Form nodes should always be selectable/unselectable
                                            style={
                                              isSourceItem
                                                ? {
                                                    accentColor: "#444444", // Dark grey for form being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : {}
                                            }
                                          />
                                          {label}
                                        </label>
                                        <div>{item.short_description}</div>
                                      </div>
                                    );
                                  }
                                  case "canvas": {
                                    const pages = getPages(item);

                                    const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                                    const nodeHeading = `Canvas ${globalCanvasNumber}`;
                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        fontSize="20px" // Use consistent font size
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "canvas",
                                          nodeIndex: globalCanvasNumber - 1,
                                        }}
                                        color="#0d6efd" // Blue color for all items
                                      />
                                    );

                                    return (
                                      <div
                                        key={item.canvas_tree_node_id || label}
                                        style={{
                                          marginTop: "40px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.canvas_tree_node_id
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                // Clear any step selection when selecting a substep
                                                setSelectedStepInfo(null);
                                                setSelectedNodeInfo({
                                                  nodeId:
                                                    item.canvas_tree_node_id,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex,
                                                  globalNodeNumber:
                                                    globalCanvasNumber,
                                                  nodeType: "Canvas",
                                                  direction: null,
                                                  substepIndex: substepIndex,
                                                  stepId,
                                                });
                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  substepId,
                                                  substepTreeId:
                                                    substep.procedure_tree_ref,
                                                  substepSiblingOrder:
                                                    substep.sibling_order ||
                                                    substep.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.canvas_tree_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                });
                                                setSelectionType("node");
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              (selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.canvas_tree_node_id &&
                                                selectedNodeInfo.nodeType ===
                                                  "Canvas") ||
                                              false
                                            }
                                            disabled={false} // Substep Canvas nodes should always be selectable/unselectable
                                            style={
                                              isSourceItem
                                                ? {
                                                    accentColor: "#444444", // Dark grey for canvas being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : {}
                                            }
                                          />
                                          {label}
                                        </label>

                                        {/* Display all pages vertically */}
                                        <CanvasDisplay pages={pages} />
                                      </div>
                                    );
                                  }
                                  case "attachment": {
                                    const attachmentList =
                                      item.attachments || [];
                                    if (attachmentList.length === 0)
                                      return null;

                                    const globalAttachmentNumber =
                                      attachmentCounter;
                                    attachmentCounter++; // Increment after using the value

                                    const nodeHeading = `Attachment ${globalAttachmentNumber}`;
                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        fontSize="20px" // Use consistent font size
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "attachment",
                                          nodeIndex: globalAttachmentNumber - 1,
                                        }}
                                        color="#0d6efd" // Blue color for all items
                                      />
                                    );

                                    return (
                                      <div
                                        key={
                                          item.attachment_tree_node_id ||
                                          `attachment-${itemIndex}`
                                        }
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.attachment_tree_node_id
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                setSelectedNodeInfo({
                                                  nodeId:
                                                    item.attachment_tree_node_id,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex,
                                                  globalNodeNumber:
                                                    globalAttachmentNumber,
                                                  nodeType: "Attachment",
                                                  direction: null,
                                                  substepIndex: substepIndex,
                                                  stepId,
                                                });
                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  substepId,
                                                  substepTreeId:
                                                    substep.procedure_tree_ref,
                                                  substepSiblingOrder:
                                                    substep.sibling_order ||
                                                    substep.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.attachment_tree_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                });
                                                setSelectionType("node");
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              (selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.attachment_tree_node_id &&
                                                selectedNodeInfo.nodeType ===
                                                  "Attachment") ||
                                              false
                                            }
                                            disabled={false} // Substep Attachment nodes should always be selectable/unselectable
                                            style={
                                              isSourceItem
                                                ? {
                                                    accentColor: "#444444", // Dark grey for attachment being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : {}
                                            }
                                          />
                                          {label}
                                        </label>
                                        <div>{item.short_description}</div>
                                        {attachmentList.map((attachment, i) => (
                                          <div
                                            key={i}
                                            style={{
                                              marginLeft: "20px",
                                              clear: "both",
                                            }}
                                          >
                                            {renderPreview(attachment)}
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                  default:
                                    return null;
                                }
                              })}
                            </div>
                          );
                        }
                      )}
                    </div>
                  );
                }
              )}
            </div>
          );
        })}

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
      <div style={{ height: "100px" }}></div>
    </div>
  );
};
export default ProcedureRichTextMove;
